<script lang="ts" setup>
import I18n from '@/addons/i18n'
import FeButton from '@/components/global/button.vue'
import FeIcon from '@/components/global/icon.vue'
import {
  defineProps,
  withDefaults,
  defineEmits,
  computed,
  defineAsyncComponent,
} from 'vue'
import { DeliveryDataD } from '@/components/drawer/extra-services/delivery-section/delivery-section.d'
import { DeliveryData } from '@/components/drawer/extra-services/delivery-section/delivery-section.data'
import modalController, { ModalTypes } from '@/components/modal/ModalController'
import { State } from '@/modules/address-validation/store/_state'
import { useStore } from 'vuex'
import {
  ActionsNames,
  AddressValidationActions,
  AddressValidationGetters,
  GetterNames,
} from '@/modules/address-validation/store'
import { ConfigGetters } from '@/store/configs-store'
import { NotificationsActions } from '@/store/notifications-store'
import { isConfigTruthy } from '@/addons/functions/configs'
const store = useStore()
const props = withDefaults(
  defineProps<{
    disabled?: boolean
    dataForm: DeliveryDataD
    isStoreAddress?: boolean
    destination: string
    isCountryUSorCA: boolean
  }>(),
  {
    disabled: true,
    dataForm: DeliveryData,
    isStoreAddress: false,
    destination: '',
    isCountryUSorCA: false,
  },
)

const getCountry = computed(() => store.getters[ConfigGetters.GET_COUNTRY_CODE])
const getStatusValidateConsumer = computed<
  State['statusValidateCheckConsumer']
>(
  () =>
    store.getters[
      AddressValidationGetters[GetterNames.GET_STATUS_CHECK_CONSUMER]
    ],
)

const getStatusValidateStore = computed<State['statusValidateCheckStore']>(
  () =>
    store.getters[AddressValidationGetters[GetterNames.GET_STATUS_CHECK_STORE]],
)

const shouldBeDisabled = computed(
  () =>
    props.disabled ||
    (getStatusValidateStore.value.success && props.isStoreAddress) ||
    (getStatusValidateConsumer.value.success && !props.isStoreAddress),
)
const emit = defineEmits([
  'update:deleteDelivery',
  'update:selectAddress',
  'update:shipping-cost',
  'update:unsaved-shipping-details-check',
])
/**
 * Checks the validation status of the delivery address and performs the appropriate actions.
 *
 * If the address is a store address, it dispatches actions to set the selected address and the status of the validation check.
 * If the address is not a store address, it opens a modal to allow the user to validate the address.
 *
 * @param {DeliveryDataD} props.dataForm - The delivery data form.
 * @param {boolean} props.isStoreAddress - Indicates whether the address is a store address.
 * @param {Function} emit - The emit function to update the parent component.
 * @returns {Promise<void>} - A promise that resolves when the validation check is complete.
 */
async function checkValidation() {
  // se indirizzo è uno store evita validazione indirizzo
  if (props.isStoreAddress) {
    await store.dispatch(
      AddressValidationActions[ActionsNames.SET_SELECTED_ADDRESS_STORE],
      {
        ...props.dataForm,
      },
    )
    store.dispatch(
      AddressValidationActions[ActionsNames.SET_ADDRESS_TYPE],
      props.destination,
    )
    emit('update:selectAddress', props.dataForm)
    emit('update:shipping-cost')
    emit('update:unsaved-shipping-details-check', false)
    await store.dispatch(
      AddressValidationActions[ActionsNames.SET_STATUS_CHECK_STORE],
      {
        success: true,
        warning: false,
      },
    )
    return
  }
  // se validazione indirizzo disabilitata
  if (!isConfigTruthy('ADDRESS_VALIDATION')) {
    await store.dispatch(
      AddressValidationActions[ActionsNames.SET_SELECTED_ADDRESS_CONSUMER],
      {
        ...props.dataForm,
      },
    )
    store.dispatch(
      AddressValidationActions[ActionsNames.SET_ADDRESS_TYPE],
      props.destination,
    )
    emit('update:selectAddress', props.dataForm)
    emit('update:shipping-cost')
    emit('update:unsaved-shipping-details-check', false)
    await store.dispatch(
      AddressValidationActions[ActionsNames.SET_STATUS_CHECK_CONSUMER],
      {
        success: true,
        warning: false,
      },
    )
    return
  }
  // se indirizzo del consumer, apri modale verifica indirizzo se la nazione di destinazione è uguale a quella dello store oppure è abilitata la vendita oss
  if (
    getCountry.value === props.dataForm.country ||
    isConfigTruthy('OSS_ENABLED')
  ) {
    modalController.open({
      type: ModalTypes.BLACK,
      component: 'FeDeliveryModal',
      pathComponent: defineAsyncComponent(
        () =>
          import(
            /* @vite-ignore */ `@/modules/address-validation/components/delivery-modal/delivery-modal.vue`
          ),
      ),
      title: I18n.global.t('pos_address.address_validation'),
      message: '',
      customClass: 'delivery-modal',
      confirmLabel: I18n.global.t('pos_common.confirm'),
      customProps: {
        insertedAddress: {
          ...props.dataForm,
          // destination: props.destination,
        },
        selectAddressAction: (data) => {
          emit('update:selectAddress', data)
          emit('update:shipping-cost')
        },
      },
      customFooter: true,
      confirmActionButton: true,
    })
  } else {
    store.dispatch(
      NotificationsActions.NOTIFY_ERROR,
      'pos_consumer.shipping_address_country_error',
    )
  }
}
function deleteDelivery() {
  if (
    getStatusValidateStore.value.success ||
    getStatusValidateConsumer.value.success
  ) {
    modalController.open({
      type: ModalTypes.WARNING,
      component: '',
      title: I18n.global.t('pos_common.attention'),
      message: I18n.global.t('pos_address.delete_address'),
      confirmLabel: I18n.global.t('pos_common.confirm'),
      confirmActionButton: true,
      confirmAction: () => {
        emit('update:deleteDelivery')
      },
    })
  } else {
    emit('update:deleteDelivery')
  }
}
</script>

<template>
  <ion-row class="delivery-button">
    <template
      v-if="!props.isStoreAddress && isConfigTruthy('ADDRESS_VALIDATION')"
    >
      <ion-col
        v-if="getStatusValidateConsumer.success"
        size="12"
        class="delivery-button__validate"
      >
        <fe-icon icon="checkmark-round-success" class-name="--green" />
        <p class="--text">
          {{ I18n.global.t('pos_address.address_validate') }}
        </p>
      </ion-col>
      <ion-col
        v-if="
          !getStatusValidateConsumer.success ||
          getStatusValidateConsumer.warning
        "
        size="12"
        class="delivery-button__validate"
      >
        <fe-icon icon="alert-circle" class-name="--yellow" />
        <p class="--text">
          {{ I18n.global.t('pos_address.address_not_validate') }}
        </p>
      </ion-col>
    </template>
    <ion-col size="6">
      <fe-button class="form__delete" color="warn" @click="deleteDelivery">
        <template #icon-right>
          <fe-icon :icon="'delete'"></fe-icon>
        </template>
        {{ I18n.global.t('pos_sale.remove_shipping') }}
      </fe-button>
    </ion-col>
    <ion-col size="6" class="delivery-button__col">
      <fe-button
        :disabled="shouldBeDisabled"
        color="medium"
        @click="checkValidation"
      >
        <template #icon-right>
          <fe-icon icon="checkmark" />
        </template>
        {{ I18n.global.t('pos_common.confirm') }}
      </fe-button>
    </ion-col>
  </ion-row>
</template>

<style scoped lang="scss">
@import '@/styles/_constants.scss';
.delivery-button {
  margin-top: 2rem;
  .--text {
    font-size: 1.6rem;
    font-weight: 400;
    margin: 0 0 0 1rem;
  }
  &__col {
    justify-content: flex-end;
    display: inline-flex;
  }
  &__validate {
    display: flex;
    align-items: center;

    :deep(.fe-icon) {
      display: inline-flex;
      .--yellow {
        color: $light-yellow;
        width: 2rem;
        height: 2rem;
        margin-left: -0.2rem;
        margin-right: -0.2rem;
      }
    }
    .--green {
      &:deep(path) {
        fill: $color-success;
      }
    }
  }
}
</style>
